import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";

const MentorAgreement = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <PageHeader 
        title="Mentor Agreement"
      />
      
      <main className="flex-grow pb-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl pt-12">
          <div className="bg-white p-8 rounded-lg shadow-sm space-y-8">
            <div>
              <h1 className="text-2xl font-bold mb-2">EMU Mentor Agreement</h1>
              <div className="text-sm text-gray-500 mb-6">
                Last updated: 16/09/2025
              </div>
            </div>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">1. Introduction & Acceptance</h2>
              <p className="text-gray-700 mb-4">
                EMU Mentoring Pty Ltd ("EMU", "we", "us") operates an online platform that connects mentors and mentees and facilitates messaging, scheduling, course access and payments (the "Platform"). EMU does not provide mentoring services and is not a party to any mentoring arrangement between a mentor and a mentee.
              </p>
              <p className="text-gray-700">
                By applying to be a mentor, creating an account, or providing mentoring via the Platform, you acknowledge you have read, understood and agree to be bound by this Mentor Agreement and our Terms of Service and Privacy Policy (together, the "Agreement"). If you accept on behalf of a company, you represent and warrant you have authority to bind that company.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">2. Relationship of the Parties</h2>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>Mentors are independent contractors.</li>
                <li>Nothing in this Agreement creates employment, agency, or partnership with EMU.</li>
                <li>You are solely responsible for how you deliver your mentoring (including tools, methods and materials).</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">3. Eligibility, Registration & Account Security</h2>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>You must be 18+ and legally capable of entering into contracts.</li>
                <li>Provide accurate, current information (including legal name and contact details) and keep it updated.</li>
                <li>EMU may conduct identity or credential checks (directly or via third parties) and you authorise such checks.</li>
                <li>You are responsible for safeguarding your login credentials and all activity on your account. Notify EMU promptly of any unauthorised use.</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">4. Use of the Platform & General Conduct</h2>
              <p className="text-gray-700 mb-4">You agree to:</p>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>Act professionally, respectfully and lawfully; no harassment, discrimination, abuse, doxxing or bullying.</li>
                <li>Provide honest, independent guidance within your competence and be transparent about limits.</li>
                <li>Keep communications within the Platform unless EMU consents otherwise.</li>
                <li>Not misuse, scrape, reverse engineer, interfere with, or attempt to circumvent the Platform or its payment flows.</li>
                <li>Comply with all applicable laws, including competition/consumer, privacy and spam laws.</li>
                <li>Not record sessions unless all participants have given explicit consent (see §11.4).</li>
                <li>Not claim or imply you provide regulated professional advice (legal, financial, medical, migration, etc.) unless you are properly qualified and the session is expressly arranged for that purpose.</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">5. Sessions, Content & Responsibilities</h2>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>You decide your availability, formats and approach, and must show up on time for confirmed bookings.</li>
                <li>You are solely responsible for all content, materials and guidance you provide.</li>
                <li>You must maintain your own equipment, software and internet connection.</li>
                <li>You must not provide sessions that are unlawful, harmful, misleading or outside agreed scope.</li>
                <li>If a mentee's conduct raises safety or legal concerns, you should cease the session and contact EMU support and, where appropriate, relevant authorities.</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">6. Off-Platform Dealings</h2>
              <p className="text-gray-700 mb-4">To protect marketplace integrity:</p>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>You must not ask mentees to pay you outside EMU.</li>
                <li>You must not accept offers from mentees to pay you directly (cash, PayPal, bank transfer, etc.).</li>
                <li>This restriction applies while your EMU account is active and for 6 months after your last session with that mentee, unless EMU gives written consent.</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">7. Payments, Fees & Payouts (Stripe Connect)</h2>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>Payments are processed via Stripe Connect. You must complete Stripe onboarding, accept Stripe's terms, and keep payout details current.</li>
                <li>Mentors set their own rates.</li>
                <li>EMU deducts a 20% platform fee plus Stripe's payment processing fee from each transaction.</li>
                <li>Subject to successful settlement and risk checks, EMU instructs Stripe to pay your net amounts by the 10th of each month for the prior month.</li>
                <li>EMU may deduct or set off refunds, chargebacks, reversals, penalties, or unpaid fees from current or future payouts.</li>
                <li>EMU is not a bank or custodian and does not hold client money. Settlement timing depends on Stripe and banking networks.</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">8. Refunds, Cancellations & Consumer Rights</h2>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>Refunds and cancellations are governed by EMU's Refund Policy and the Australian Consumer Law (ACL).</li>
                <li>If a refund is approved, EMU may (where permitted by law) refund the amount paid less any non-refundable payment processor fees.</li>
                <li>Publish fair cancellation/rescheduling rules and honour them. Repeated last-minute cancellations or no-shows may result in suspension.</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">9. Taxes & Invoices</h2>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>You are responsible for your own taxes, superannuation and other statutory obligations.</li>
                <li>If you are GST-registered, include GST in your pricing and issue valid tax invoices. EMU may issue recipient-created tax invoices where agreed and permitted.</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">10. Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                <strong>Your IP:</strong> You retain ownership of your pre-existing materials. You grant EMU a worldwide, non-exclusive, royalty-free licence to host, display and transmit your profile and session materials as needed to operate the Platform.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>EMU IP:</strong> The Platform, software, brand and design are EMU's or its licensors' IP. You must not copy, modify, reverse engineer or create derivative works.
              </p>
              <p className="text-gray-700">
                <strong>Mentee Materials:</strong> Do not use a mentee's materials beyond the mentoring purpose without their permission.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">11. Privacy, Data & Recordings</h2>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>Comply with the Privacy Act 1988 (Cth) and Australian Privacy Principles to the extent applicable to you.</li>
                <li>Use personal information only for mentoring via the Platform, keep it secure, and delete it when no longer required (subject to legal retention obligations).</li>
                <li>Do not add mentees to marketing lists without consent (see Spam Act 2003 (Cth)).</li>
                <li><strong>Recordings:</strong> No audio/video recording unless all participants consent. If recorded, store securely, restrict access, use only for the stated purpose, and delete on request unless retention is required by law.</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">12. Confidentiality</h2>
              <p className="text-gray-700">
                Each party must keep the other party's Confidential Information secret, use it only for mentoring/Platform purposes, and not disclose it except to personnel under similar obligations or as required by law. Confidentiality survives termination.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">13. Reviews & Community Standards</h2>
              <p className="text-gray-700">
                EMU may host ratings/reviews and community features. Do not post or solicit false, misleading or defamatory reviews. EMU may moderate or remove content that breaches law or policy.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">14. Disclaimers & Australian Consumer Law</h2>
              <p className="text-gray-700">
                Except for Non-Excludable Guarantees under the ACL, the Platform and related EMU services are provided "as is" and "as available". EMU does not promise uninterrupted or error-free operation. Where our liability for breach of a Non-Excludable Guarantee can be limited, it is limited to resupplying the services or paying the cost of resupply.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">15. Indemnity</h2>
              <p className="text-gray-700">
                You indemnify EMU and its personnel against losses, costs, claims and liabilities arising from: (a) your mentoring, content or conduct; (b) your breach of this Agreement or the law; or (c) infringement of third-party rights, except to the extent caused by EMU's wilful misconduct.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">16. Limitation of Liability</h2>
              <p className="text-gray-700">
                To the maximum extent permitted by law, EMU is not liable for indirect or consequential loss, loss of profit, revenue, data, goodwill or business interruption. EMU's aggregate liability to you under or in connection with this Agreement is capped at the greater of AUD $500 or the total amount you paid to EMU in the 12 months preceding the event giving rise to the claim, except where the ACL prevents limitation.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">17. Suspension & Termination</h2>
              <p className="text-gray-700">
                EMU may suspend or terminate your account immediately if you breach this Agreement, create risk, or where required by law. You may close your account at any time (outstanding obligations survive, including refunds/chargebacks and confidentiality). EMU may retain data as permitted by law and by our Privacy Policy.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">18. Changes to this Agreement</h2>
              <p className="text-gray-700">
                We may update this Agreement by posting a revised version on the Platform or notifying you. Material changes (e.g., to fees) will be notified in advance where reasonable. Continued use after changes take effect constitutes acceptance.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">19. Governing Law & Disputes</h2>
              <p className="text-gray-700">
                This Agreement is governed by the laws of Queensland, Australia. The parties will first negotiate in good faith for 30 days, then refer unresolved disputes to mediation (e.g., Resolution Institute Mediation Rules) in Brisbane, QLD, before litigation. The parties submit to the exclusive jurisdiction of the Queensland courts.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">20. Notices</h2>
              <p className="text-gray-700">
                We may provide notices by email to your registered address or via in-Platform notifications. You are responsible for keeping your contact details current.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">21. General</h2>
              <p className="text-gray-700 mb-4">
                <strong>Assignment:</strong> You must not assign or transfer this Agreement without EMU's consent. EMU may assign to a related body corporate or acquirer.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Severance:</strong> If any provision is invalid, it will be severed and the remainder continues.
              </p>
              <p className="text-gray-700">
                <strong>Entire Agreement:</strong> This Agreement applies in addition to the EMU Terms of Service and Privacy Policy and, together, they form the entire agreement between EMU and the Mentor about the Platform.
              </p>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default MentorAgreement;